import type { z } from 'zod';

import { asRecord, toString } from '@sites/entity-migrator';

import type { lineDefaultV0Schema } from '../default/v0';
import { createdAtOrNull, oneOf, parseNumber, responsiveValue, stringOrUndefined } from '../../../common/migration';
import { createLineStylesLegacy } from './styles';

type LineDefaultV0Input = z.input<typeof lineDefaultV0Schema>;

const HORIZONTAL_ALIGNS = ['L', 'M', 'R'] as const;
const BORDER_STYLES = ['solid', 'dashed', 'dotted'] as const;

/** The seed a line has always been created with — what a missing or unreadable legacy value falls back to. */
const SEED = createLineStylesLegacy();

/**
 * Lift a pre-versioning line to v0. `data.horizontalAlign` and the `styles` bag
 * become the `desktop` leaves, the matching `mobileStyles` keys the `mobile`
 * ones; keys a line has no field for are dropped. `margin` stays `null` where
 * legacy had none — the renderer applied no margin then, and still won't.
 */
export const migrateLegacyLineToV0 = (input: unknown): LineDefaultV0Input => {
    const legacy = asRecord(input);
    const data = asRecord(legacy['data']);
    const styles = asRecord(legacy['styles']);
    const mobile = asRecord(legacy['mobileStyles']);

    return {
        id: toString(legacy['id']),
        parentId: toString(legacy['structureId']) || toString(legacy['columnId']),
        entityType: 'widget:line',
        entitySubType: 'default',
        version: 0,
        meta: {
            displayId: stringOrUndefined(legacy['displayId']) ?? null,
            createdAt: createdAtOrNull(legacy['createdAt']),
        },
        props: {
            horizontalAlign: { desktop: oneOf(HORIZONTAL_ALIGNS, data['horizontalAlign']) ?? 'M' },
            borderTopColor: responsiveValue(
                stringOrUndefined(styles['borderTopColor']) ?? SEED.borderTopColor,
                stringOrUndefined(mobile['borderTopColor'])
            ),
            borderTopWidth: responsiveValue(
                parseNumber(styles['borderTopWidth']) ?? SEED.borderTopWidth,
                parseNumber(mobile['borderTopWidth'])
            ),
            borderTopStyle: responsiveValue(
                oneOf(BORDER_STYLES, styles['borderTopStyle']) ?? SEED.borderTopStyle,
                oneOf(BORDER_STYLES, mobile['borderTopStyle'])
            ),
            width: responsiveValue(parseNumber(styles['width']) ?? SEED.width, parseNumber(mobile['width'])),
            margin: responsiveValue(stringOrUndefined(styles['margin']) ?? null, stringOrUndefined(mobile['margin'])),
        },
    };
};
